import type { SagaIterator } from 'redux-saga';
import { delay, put } from 'redux-saga/effects';
import { call as typedCall, select as typedSelect } from 'typed-redux-saga';
import * as Sentry from '@sentry/browser';
import { selectProfileOrganizationId } from 'flowhawk-core';

import api from '../Services/Api';
import { finishLoading, startLoading } from '../Redux/LoadingActions';
import { createTeamFailed, receiveTeams, resetTeamEditState } from '../Redux/TeamActions';
import { apiFetch, apiDelete, apiPost, apiPut } from './ApiSaga';
import { pushError, setSagaMessage } from '../Redux/ApplicationActions';
import parseProfile from '../Utils/profile';
import type { CreateTeam, DeleteTeam, TeamErrors, UpdateTeam } from '../Redux/TeamActions';
import type { ApiTeam, ApiTeams } from '../Utils/apiTypes';
import { InvalidResponseCode, ResourceErrorInterface } from 'tg-resources';
import type { RootState } from '../Redux';
import type { UserProfile } from '../Utils/types';
import { router } from '..';

export function* getTeamsSaga(): SagaIterator<void> {
  yield put(startLoading('teams'));

  const organizationId = yield* typedSelect((state: RootState) => selectProfileOrganizationId(state.auth));
  const query = organizationId ? { orgId: organizationId } : undefined;
  const { result, error } = yield* typedCall(apiFetch<ApiTeams>, api.txp.teams, undefined, query);

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error.isValidationError) {
        const errorMessage = error || 'Something went wrong';
        yield put(pushError(`${errorMessage}`));
      } else if (error.isNetworkError) {
        yield put(pushError('Loading teams failed, are you online?', error));
      } else {
        Sentry.captureException(error, { extra: { errorMsg: 'GetTeamsSaga() Failed' } });
      }
    } else {
      Sentry.captureException(error, { extra: { errorMsg: 'GetTeamsSaga() Failed' } });
    }
  } else {
    const teamsResult = result.teams;

    const teams = [];
    for (let i = 0; i < teamsResult.length; i += 1) {
      const tempTeam = teamsResult[i];
      const teamMembers = [];
      for (let j = 0; j < tempTeam.team_members.length; j += 1) {
        const parsedMember = parseProfile(tempTeam.team_members[j].user_information);
        teamMembers.push(parsedMember);
      }
      teams.push({
        teamId: tempTeam.team_id,
        creatorId: tempTeam.creator_id || -1,
        teamName: tempTeam.team_name,
        shared: tempTeam.shared,
        teamMembers,
        rights: tempTeam.rights,
      });
    }

    yield put(receiveTeams(teams));
  }
  yield put(finishLoading('teams'));
}

export function* createTeamSaga(action: CreateTeam): SagaIterator<void> {
  const { teamName, teamMembers, shared } = action;

  // Ensure takeLatest has time to handle doubleclicks
  //  https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  yield put(startLoading('saveTeam'));

  const userIds = [];
  for (let i = 0; i < teamMembers.length; i += 1) {
    if (teamMembers[i].userId != null) {
      userIds.push(teamMembers[i].userId);
    }
  }

  const { result, error } = yield* typedCall(apiPost<ApiTeam>, api.txp.createTeam, undefined, {
    team_name: teamName,
    team_member_ids: userIds,
    shared,
  });

  if (error || !result) {
    const errors: TeamErrors = {};
    if (error instanceof ResourceErrorInterface) {
      if (error.isInvalidResponseCode) {
        errors.teamName = 'Team with given name already exists';
        yield put(setSagaMessage('', errors.teamName, ''));
        yield put(createTeamFailed(errors));
      } else if (error.isNetworkError) {
        const errorMessage = 'Creating a team failed, are you online?';
        yield put(pushError(errorMessage, error));
        yield put(setSagaMessage('', errorMessage, ''));
      } else {
        yield put(pushError('Creating a team failed, try again later', error));
        yield put(setSagaMessage('', 'Team creation failed!', ''));
      }
    } else {
      yield put(pushError('Creating a team failed, try again later', error));
      yield put(setSagaMessage('', 'Team creation failed!', ''));
    }
    yield put(finishLoading('saveTeam'));
  } else {
    const teams = yield* typedSelect((state: RootState) => state.team.teams || []);

    const members: UserProfile[] = [];
    for (let i = 0; i < result.team_members.length; i += 1) {
      const parsedMember = parseProfile(result.team_members[i].user_information);
      members.push(parsedMember);
    }
    teams.push({
      teamId: result.team_id,
      creatorId: result.creator_id || -1,
      teamName: result.team_name,
      teamMembers: members,
      shared: result.shared,
      rights: result.rights,
    });

    yield put(receiveTeams(teams));

    yield put(resetTeamEditState());
    yield put(finishLoading('saveTeam'));
    yield* typedCall([router, router.navigate], { to: '/teams/$teamId', params: { teamId: result.team_id } });
  }
}

export function* deleteTeamSaga(action: DeleteTeam): SagaIterator<void> {
  const { teamId } = action;

  // Ensure takeLatest has time to handle doubleclicks
  //  https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  yield put(startLoading('deleteTeam'));

  const { error } = yield* typedCall(apiDelete, api.txp.team, {
    teamId,
  });

  if (error) {
    const toastMessage = 'Deleting team failed!';
    yield put(setSagaMessage('', toastMessage, ''));
    if (error instanceof ResourceErrorInterface) {
      if (error.isValidationError) {
        const errorMessage = error || 'Something went wrong';
        yield put(pushError(`${errorMessage}`));
      } else if (error.isNetworkError) {
        yield put(pushError('Deleting team failed, are you online?', error));
      } else {
        yield put(pushError('Deleting team failed, try again later', error));
      }
    } else {
      yield put(pushError('Deleting team failed, try again later', error));
    }
    yield put(finishLoading('deleteTeam'));
  } else {
    const toastMessage = 'Team deletion successful';
    yield put(setSagaMessage('', toastMessage, ''));
    const teams = yield* typedSelect((state: RootState) => state.team.teams);

    const index = teams.findIndex((x) => x.teamId === teamId);

    teams.splice(index, 1);

    yield put(receiveTeams(teams));

    yield put(finishLoading('deleteTeam'));
  }
}

export function* updateTeamSaga(action: UpdateTeam): SagaIterator<void> {
  const { teamId, teamName, teamMembers, shared } = action;

  // Ensure takeLatest has time to handle doubleclicks
  //  https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  yield put(startLoading('saveTeam'));

  const teamMemberIds = teamMembers.map((member) => member.userId);

  const { result, error } = yield* typedCall(
    apiPut<ApiTeam>,
    api.txp.updateTeam,
    {
      teamId,
    },
    {
      team_name: teamName,
      team_member_ids: teamMemberIds,
      shared,
    },
  );

  if (error || !result) {
    let toastMessage = 'Team update failed!';
    if (error instanceof InvalidResponseCode) {
      if (error.statusCode === 404) {
        yield put(pushError('Team with given ID does not exist'));
      } else if (error.statusCode === 409) {
        toastMessage = 'Team name already exists';
        yield put(pushError('Team with given name already exists'));
      } else if (error.statusCode === 403) {
        yield put(pushError('User does not have permission to update team with given ID'));
      } else if (error.statusCode === 400) {
        yield put(pushError('Invalid request'));
      } else if (error.isNetworkError) {
        toastMessage = 'Updating team failed, are you online?';
        yield put(pushError('Updating team failed, are you online?', error));
      } else {
        yield put(pushError('Updating team failed, try again later', error));
      }
    } else {
      yield put(pushError('Updating team failed, try again later', error));
    }
    yield put(setSagaMessage('', toastMessage, ''));
    yield put(finishLoading('saveTeam'));
  } else {
    const toastMessage = 'Team update successful';
    yield put(setSagaMessage('', toastMessage, ''));
    const members = [];
    for (let i = 0; i < result.team_members.length; i += 1) {
      const parsedMember = parseProfile(result.team_members[i].user_information);
      members.push(parsedMember);
    }
    const teams = yield* typedSelect((state: RootState) => state.team.teams);

    const index = teams.findIndex((x) => x.teamId === teamId);

    teams[index] = {
      ...teams[index],
      teamMembers: members,
      teamName: result.team_name,
      shared: result.shared,
      creatorId: result.creator_id,
    };

    yield put(receiveTeams(teams));
    yield put(resetTeamEditState());
    yield put(finishLoading('saveTeam'));
  }
}
